import { StatesModule } from '@uirouter/angular';
import { Transition } from '@uirouter/core';

import { AccessGuardService } from '../auth/services/access-guard.service';
import { CanAccessAppsService } from '../auth/services/can-access-apps.service';
import { UrlStateService } from '../auth/services/url-state.service';
import { ViewportComponent } from './viewport/viewport.component';
import { ScreenSharingComponent } from './components/screen-sharing/screen-sharing.component';
import { ModeratorJoinComponent } from './components/moderator-join/moderator-join.component';
import { ModeratorRoomComponent } from './components/moderator-room/moderator-room.component';
import { ScreenSharingService } from './services/screen-sharing.service';

export const routes: StatesModule = {
  states: [
    {
      name: 'apps.screen-sharing',
      abstract: true,
      component: ViewportComponent,
      resolve: [
        {
          token: 'screenSharing',
          deps: [ScreenSharingService],
          resolveFn: (screenSharingService) =>
            screenSharingService.clearErrors(),
        },
      ],
    },
    {
      name: 'apps.screen-sharing.home',
      url: '/join?rvshare&joinCode&memberDisplayName&rejoin',
      component: ScreenSharingComponent,
      data: {
        rvshareAppEnabled: true,
        requiresAuth: false,
      },
    },
    {
      name: 'apps.screen-sharing.moderator-join',
      url: '/screen-sharing/join/{folderId:any}?rvshare&tab',
      params: {
        // Dynamic: folder/tab URL changes do not destroy this view — onSuccess calls load().
        folderId: {
          dynamic: true,
          value: null,
        },
        tab: {
          dynamic: true,
          value: null,
        },
      },
      component: ModeratorJoinComponent,
      data: {
        rvshareAppEnabled: true,
        requiresAuth: false,
      },
      resolve: [
        {
          token: 'canAccess',
          deps: [Transition, CanAccessAppsService, UrlStateService],
          resolveFn: (
            transition: Transition,
            canAccessAppsService: CanAccessAppsService,
            urlStateService: UrlStateService,
          ) => {
            // Signing in returns to this page's own URL. A fixed path matched
            // no state once the folder was added to it, and the router's
            // fallback sent the moderator to the home page instead.
            const redirectTo = urlStateService.urlToState(
              transition.router.stateService.href(
                transition.to(),
                transition.params(),
              ),
            );
            return canAccessAppsService.canAccessApps(
              undefined,
              undefined,
              redirectTo,
            );
          },
        },
        {
          token: 'requireScreenSharingAccess',
          // Depends on canAccess so the check runs after authentication. This
          // state skips the global requiresAuth hook, and resolves without a
          // dependency between them run concurrently.
          deps: [Transition, AccessGuardService, 'canAccess'],
          resolveFn: (transition, accessGuardService: AccessGuardService) =>
            accessGuardService.requireScreenSharingAccess(transition),
        },
      ],
    },
    {
      name: 'apps.screen-sharing.moderator-room',
      url: '/screen-sharing/room/:displayId/:sessionId?rvshare&existingSession',
      params: {
        existingSession: { dynamic: true },
      },
      component: ModeratorRoomComponent,
      data: {
        rvshareAppEnabled: true,
      },
      resolve: [
        {
          token: 'requireScreenSharingAccess',
          deps: [Transition, AccessGuardService],
          resolveFn: (transition, accessGuardService: AccessGuardService) =>
            accessGuardService.requireScreenSharingRoomAccess(transition),
        },
      ],
    },
  ],
};
